import React, { useState } from 'react';
import cx from 'classnames';
import { createPortal } from 'react-dom';

import Card from '@marketing/components/card';
import { QuoteCard } from '@marketing/components/testimonial_card';
import SectionHeader from '@marketing/components/section_header';
import UnderlinedText from '@marketing/components/underlined_text';

import HeartIcon from './assets/heart_icon.svg';
import colors from '@magoosh/lib/styles/colors.module.scss';
import marketingStyles from '@marketing/style.module.scss';
import styles from './style.module.scss';

interface FeaturedQuote extends Quote {
  image: {
    alt?: string;
    height: string;
    src: string;
    width: string;
  };
  video?: {
    sources: {
      src: string;
      type: string;
    }[];
  };
}

interface Quote {
  content: string | React.ReactNode;
  studentAffiliation: string;
  studentName: string;
}

interface Props {
  featuredQuote: FeaturedQuote;
  quotes: Quote[];
  showAllQuotesOnMobile?: boolean;
  title?: React.ReactNode;
}

const WallOfLove: React.FC<Props> = ({ featuredQuote, quotes, showAllQuotesOnMobile, title }) => {
  const [displayVideoPlayer, setDisplayVideoPlayer] = useState<boolean>(false);

  return (
    <div className="d-flex flex-column gap-l" style={{ position: 'relative' }}>
      <div
        className={cx(marketingStyles.fullPageWidthContainerBreakout, marketingStyles.purpleGradientBg)}
        style={{ position: 'absolute', height: 900, top: -50, zIndex: -1 }}
      />

      <SectionHeader
        subtitle="Check out what others say about us."
        supertitle="Wall of love"
        supertitleIcon={HeartIcon}
        title={title || <>More students choose <UnderlinedText color={colors.brandSecondary}>Magoosh</UnderlinedText> than anyone else.</>}
      />

      <div className={cx(styles.wallOfLoveContainer, 'd-flex flex-column gap-m')}>
        <QuoteCard
          image={{
            ...featuredQuote.image,
            onClick: featuredQuote.video ? () => setDisplayVideoPlayer(true) : null
          }}
          studentAffiliation={featuredQuote.studentAffiliation}
          studentName={featuredQuote.studentName}
        >
          {featuredQuote.content}
        </QuoteCard>

        <div className={cx({ [styles.quoteCardsContainer]: !showAllQuotesOnMobile }, 'd-flex flex-row flex-wrap gap-m')}>
          {
            quotes.map((quote, idx) => (
              <QuoteCard
                key={idx}
                className={styles.quoteCard}
                studentAffiliation={quote.studentAffiliation}
                studentName={quote.studentName}
              >
                {quote.content}
              </QuoteCard>
            ))
          }
        </div>
      </div>

      {
        displayVideoPlayer && featuredQuote.video && (
          createPortal(
            <>
              <div
                className={styles.pageOverlay}
                onClick={() => setDisplayVideoPlayer(false)}
              />

              <div className={cx(marketingStyles.purpleNoiseBg, styles.videoPlayerContainer)}>
                <div className={cx(styles.videoPlayerWrapper, 'd-flex flex-column')}>
                  <div
                    className="align-items-center bg-white d-flex gap-s"
                    style={{ borderRadius: '12px 12px 0 0', padding: 10 }}
                  >
                    <div className="flex-1 small"><strong>{featuredQuote.studentName}</strong> {featuredQuote.studentAffiliation}</div>

                    <div>
                      <a className="small text-body" onClick={() => setDisplayVideoPlayer(false)}>
                        close video
                      </a>
                    </div>
                  </div>

                  <video autoPlay className={styles.videoPlayer} controls style={{ borderRadius: '0 0 12px 12px' }}>
                    {
                      featuredQuote.video.sources.map((source, index) => (
                        <source key={index} src={source.src} type={source.type} />
                      ))
                    }
                  </video>
                </div>
              </div>
            </>,
            document.body
          )
        )
      }
    </div>
  );
};

export default WallOfLove;
